import premium from "../../assets/premium-badge.svg"
import gold from "../../assets/gold-badge.svg"
import platinum from "../../assets/platinum.svg"
import { useSelector } from "react-redux";
import { SET_SHOW_LOGIN } from "@/redux/features/auth/authSlice";
import { useDispatch } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { logEvent } from "firebase/analytics";
import { analytics } from "@/firebase";
import { CircleCheck, Flame, Rocket, Star } from "lucide-react";
import { trackMetaEvent } from '@/utils/metaPixel'
import { trackLinkedInEvent } from '@/utils/linkedinTag'
import { trackRedditCustomEvent } from '@/utils/redditPixel'
import { trackTwitterEvent } from '@/utils/twitterPixel'

const SubscribeCard = () => {
    const { user } = useSelector(state => state.auth);
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)
    const [pendingPlan, setPendingPlan] = useState(null)
    // Updated pricing to match Subscription.jsx
    const platinumPrice = 29.99;
    const goldPrice = 14.99;
    const premiumPrice = 9.99;
    const [isYearly, setIsYearly] = useState(false);

    // Calculate yearly prices with 19.91% discount to match Subscription.jsx
    const calculateYearlyPrice = (monthlyPrice) => {
        const yearlyPrice = monthlyPrice * 12;
        const discount = yearlyPrice * 0.1991;
        return (yearlyPrice - discount).toFixed(0) + '.00';
    };
    
    // Add this function to calculate monthly price from yearly price
    const calculateMonthlyFromYearly = (monthlyPrice) => {
        const yearlyPrice = monthlyPrice * 12;
        const discount = yearlyPrice * 0.1991;
        return ((yearlyPrice - discount) / 12).toFixed(2);
    };
    
    // Get price based on subscription type
    const getPrice = useCallback((basePrice) => {
        return isYearly ? calculateYearlyPrice(basePrice) : basePrice;
    }, [isYearly]);

    // Add this function to get monthly equivalent price
    const getMonthlyEquivalent = useCallback((basePrice) => {
        return isYearly ? calculateMonthlyFromYearly(basePrice) : basePrice;
    }, [isYearly]);

    const buyPremium = useCallback(async (plan, yearly = false) => {
        if (!user || user?.is_guest) {
            setPendingPlan({ plan, yearly: isYearly })
            dispatch(SET_SHOW_LOGIN(true))
            return toast.error("Please login to continue", { id: "premium" })
        }
        const token = window.localStorage.getItem('token')
        const utmParams = JSON.parse(localStorage.getItem('utmParams')) || {};
        const utmSource = utmParams.utm_source || 'unknown';
        const utmMedium = utmParams.utm_medium || 'unknown';
        const utmCampaign = utmParams.utm_campaign || 'unknown';
        const utmContent = utmParams.utm_content || 'unknown';
        
        // Determine product_id based on plan
        let productIdString = '';
        if (plan === 1) {
            productIdString = 'Premium';
        } else if (plan === 2) {
            productIdString = 'Gold';
        } else if (plan === 3) {
            productIdString = 'Platinum';
        } else {
            toast.error("Invalid plan selected", { id: "premium" });
            return;
        }
        
        // Add yearly suffix if applicable
        if (yearly) {
            if (plan === 1) {
                productIdString = 'Premium_yearly';
            } else if (plan === 2) {
                productIdString = 'Gold_yearly';
            } else if (plan === 3) {
                productIdString = 'Platinum_yearly';
            }
        }

        try {
            setLoading(true)
            toast.loading("Opening Payment Page", { id: "premium" })
            // Updated endpoint and payload format
            const response = await axios.post("/api/subscriptions/create-checkout", { product_id: productIdString }, { headers: { Authorization: `Bearer ${token}` } })
            logEvent(analytics, 'begin_checkout', {
                currency: 'USD',
                plan: `plan_${plan}`,
                plan_name: productIdString,
                source: utmSource,
                medium: utmMedium,
                campaign: utmCampaign,
                content: utmContent,
            });
            
            // Add tracking for different sources
            if (utmSource.includes('meta')) {
                trackMetaEvent('InitiateCheckout', {
                    currency: 'USD',
                    plan: `plan_${plan}`,
                    plan_name: productIdString,
                })
            } else if (utmSource.includes('linkedin')) {
                trackLinkedInEvent('begin_checkout', {
                    currency: 'USD',
                    plan: `plan_${plan}`,
                    plan_name: productIdString,
                    source: utmSource,
                    medium: utmMedium,
                    campaign: utmCampaign,
                    content: utmContent,
                    event: 'begin_checkout',
                });
            } else if (utmSource.includes('reddit')) {
                trackRedditCustomEvent('begin_checkout', {
                    currency: 'USD',
                    plan: `plan_${plan}`,
                    plan_name: productIdString,
                });
            } else if (utmSource.includes('twitter')) {
                trackTwitterEvent('tw-p774a-p8bkd', {
                    currency: 'USD',
                    value: getPrice(plan === 1 ? premiumPrice : plan === 2 ? goldPrice : platinumPrice),
                });
            }
            
            window.location.href = response.data.checkout_url
            setLoading(false)
        } catch (error) {
            setLoading(false)
            toast.error(error?.response?.data?.detail || error.message || "Oops! Error Occured", { id: "premium" });
            console.log(error);
        }
    }, [dispatch, user, isYearly, premiumPrice, goldPrice, platinumPrice, getPrice])

    useEffect(() => {
        if (user && !user.is_guest && pendingPlan) {
            buyPremium(pendingPlan.plan, pendingPlan.yearly);
            setPendingPlan(null);
        }
    }, [user, pendingPlan, buyPremium])

    const plans = [
        {
            id: 1,
            name: 'Premium Plan',
            price: premiumPrice,
            image: premium,
            description: 'Perfect for hobbyists and music enthusiasts taking their first step into AI music creation.',
            active: user?.plan === 1,
            show: !user || user?.is_guest || (user.plan < 1),
            benifits: [
                <Benifit key={1} text={`2,000 ${isYearly ? 'Monthly' : ''} Credits`} />,
                <Benifit key={2} text="200 Daily Credits" />,
                <Benifit key={3} text="Affiliate Program" />,
                <Benifit key={4} text="Extend & Clone Songs" />,
                <Benifit key={5} text="Launch Your Song Token" />,
                <Benifit key={6} text="Generate Songs from url" />,
                <Benifit key={7} text="Standard Customer Support" />,
                <Benifit key={8} text="Generate Songs from Images" />,
                <Benifit key={9} text="2 Simultaneous Song Generations" />
            ]
        },
        {
            id: 2,
            name: 'Gold Plan',
            price: goldPrice,
            image: gold,
            description: 'Designed for creators who want more freedom in their music projects.',
            active: user?.plan === 2,
            show: !user || user?.is_guest || (user.plan < 2),
            benifits: [
                <Benifit2 key={1} text={`5,000 ${isYearly ? 'Monthly' : ''} Credits`} />,
                <Benifit2 key={2} text="200 Daily Credits" />,
                <Benifit2 key={3} text="Affiliate Program" />,
                <Benifit2 key={4} text="Extend & Clone Songs" />,
                <Benifit2 key={5} text="Launch Your Song Token" />,
                <Benifit2 key={6} text="Generate Songs from url" />,
                <Benifit2 key={7} text="Standard Customer Support" />,
                <Benifit2 key={8} text="Generate Songs from Images" />,
                <Benifit2 key={9} text="3 Simultaneous Song Generations" />
            ]
        },
        {
            id: 3,
            name: 'Platinum Plan',
            price: platinumPrice,
            image: platinum,
            description: 'The ultimate plan for professionals and studios producing high-volume AI music.',
            active: user?.plan === 3,
            show: !user || user?.is_guest || (user.plan < 3),
            benifits: [
                <Benifit3 key={1} text={`20,000 ${isYearly ? 'Monthly' : ''} Credits`} />,
                <Benifit3 key={2} text="200 Daily Credits" />,
                <Benifit3 key={3} text="Affiliate Program" />,
                <Benifit3 key={4} text="Extend & Clone Songs" />,
                <Benifit3 key={5} text="Launch Your Song Token" />,
                <Benifit3 key={6} text="Generate Songs from url" />,
                <Benifit3 key={7} text="Publish Songs on Spotify" />,
                <Benifit3 key={8} text="Priority Customer Support" />,
                <Benifit3 key={9} text="Generate Songs from Images" />,
                <Benifit3 key={10} text="5 Simultaneous Song Generations" />
            ]
        }
    ];

    return (
        <div>
            <div className="flex flex-col items-center justify-center w-max bg-transparent p-0 mx-auto mt-4">
                <div className="flex items-center justify-center gap-3 mt-4 bg-slate-900 backdrop-blur-lg rounded-full border border-slate-800/20">
                    <div className="relative">
                        <button onClick={() => setIsYearly(false)} className={`relative sm:px-8 px-6 py-2 text-sm sm:text-base rounded-full font-semibold ${!isYearly ? 'bg-gradient-to-r from-green-400 to-teal-400 text-slate-950 shadow-lg border-1 border-green-400/20' : 'bg-transparent hover:bg-green-800/40 text-green-400 hover:text-green-300 hover:border-green-600/50'}`}>Monthly</button>
                    </div>
                    <div className="relative">
                        {isYearly && (
                            <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-green-400 flex items-center justify-center text-xs font-bold sm:px-4 px-2 py-1 bg-transparent rounded-full shadow-md whitespace-nowrap">
                                <Star className='mr-2 text-yellow-500 fill-yellow-500' size={20} />
                                SAVE 19.99% OFF
                            </div>
                        )}
                        <button onClick={() => setIsYearly(true)} className={`relative sm:px-8 px-6 py-2 text-sm sm:text-base rounded-full font-semibold ${isYearly ? 'bg-gradient-to-r from-green-400 to-teal-400 text-slate-950 shadow-lg border-1 border-green-400/20' : 'bg-transparent hover:bg-green-800/40 text-green-400 hover:text-green-300 hover:border-green-600/50'}`}>Yearly</button>
                    </div>
                </div>
                <div className={`grid grid-cols-1 ${user?.plan === 1 ? 'lg:grid-cols-2' : user?.plan === 2 ? 'lg:grid-cols-1' : 'lg:grid-cols-2 xl:grid-cols-3'} justify-items-center gap-4 mt-4 w-full mx-auto`}>
                    {[...plans].reverse().map(plan => plan.show && (
                        <div 
                            key={plan.id} 
                            className={`relative w-full rounded-3xl flex flex-col items-center justify-center max-w-xs py-4 px-8 backdrop-blur-lg border ${
                                plan.id === 1 ? 
                                'bg-gradient-to-bl from-green-950/50 via-slate-900/50 to-slate-900/50 border-green-700/20' :
                                plan.id === 2 ?
                                'bg-gradient-to-b from-green-950/80 via-slate-900/50 to-slate-900/50 border-amber-900/20' :
                                'bg-gradient-to-br from-green-950/50 via-slate-900/50 to-slate-900/50 border-slate-400/20'
                            }`}
                        >
                            {plan.active && (
                                <div className='absolute top-0 left-0 border rounded border-green-400/80 dark:border-pink-500 text-green-400 dark:text-pink-700 rounded-tl-3xl rounded-br-3xl px-4 py-1.5 text-xs font-medium bg-green-400/10 dark:bg-pink-400/10'>
                                    Active Plan
                                </div>
                            )}
                            {plan.id === 2 && (
                                <div className='absolute top-0 right-0 border rounded border-green-400/80 dark:border-pink-500 text-green-400 dark:text-pink-700 rounded-tr-3xl rounded-bl-3xl px-4 py-1.5 text-xs font-medium bg-green-400/10 dark:bg-pink-400/10'>
                                    Most Popular
                                </div>
                            )}
                            <div className='flex items-center justify-center'>
                                <img src={plan.image} alt={plan.name.toLowerCase()} className='object-cover dark:opacity-90' />
                            </div>
                            <h3 className='text-white dark:text-slate-800 mt-6'>{plan.name}</h3>
                            <p className='text-xs w-full text-center mx-auto text-slate-400 dark:text-slate-600 mt-2'>
                                {plan.description}
                            </p>
                            <div className='pt-2 pb-3'>
                                <div className='flex flex-col items-center justify-center gap-1'>
                                    {isYearly && (
                                        <>
                                            <div className="flex items-center justify-center gap-2 text-sm text-red-400">
                                                <span className="line-through text-red-400">
                                                    ${(plan.price * 12).toFixed(2)}
                                                </span>
                                                <span className="savings text-green-400 text-sm">
                                                    Save ${(plan.price * 12 * 0.1991).toFixed(2)}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                    <div className='flex items-center justify-center gap-1 text-2xl font-medium text-green-400'>
                                        <span>$</span>
                                        <span>{isYearly ? getMonthlyEquivalent(plan.price) : plan.price}</span>
                                        <span>/month</span>
                                    </div>
                                    {isYearly && (
                                        <div className='text-xs text-slate-400 dark:text-slate-600'>
                                            <span className="text-slate-400 text-xs">Billed as ${getPrice(plan.price)}/year</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={() => buyPremium(plan.id, isYearly)}
                                disabled={loading}
                                className={`text-sm border border-slate-800 hover:text-black bg-gradient-to-b from-slate-800/90 to-transparent w-full text-white dark:text-black h-10 rounded-lg cursor-pointer transition-all duration-300 relative ${plan.id === 1 && 'hover:from-green-400 hover:to-green-400 hover:text-black'} ${plan.id === 2 && 'hover:from-amber-400 hover:to-amber-400 hover:text-black'} ${plan.id === 3 && 'hover:from-slate-100 hover:to-slate-100 hover:text-black'}`}
                            >
                                <span className="absolute bottom-8 -right-1 transform rotate-12 text-lg">👑</span>
                                Subscribe
                            </button>
                            <div className='h-px w-full bg-slate-800 dark:bg-pink-400/20 mt-8 mb-6'></div>
                            <div className='flex flex-col gap-2'>
                                {plan.benifits.map((benifit, index) => (
                                    <div key={index}>{benifit}</div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default SubscribeCard

function Benifit({ text }) {
    return (
        <div className='flex items-start justify-start gap-2'>
            <CircleCheck size={18} strokeWidth={1.5} className="text-green-300 dark:text-pink-700" />
            <p className='text-sm text-slate-400 dark:text-slate-800 text-left'>{text}</p>
        </div>
    )
}
function Benifit2({ text }) {
    return (
        <div className='flex items-start justify-start gap-2'>
            <Flame size={18} strokeWidth={1.5} className="text-amber-300 dark:text-pink-700" />
            <p className='text-sm text-slate-400 dark:text-slate-800 text-left'>{text}</p>
        </div>
    )
}
function Benifit3({ text }) {
    return (
        <div className='flex items-start justify-start gap-2'>
            <Rocket size={18} strokeWidth={1.5} className="text-purple-300 dark:text-pink-700" />
            <p className='text-sm text-slate-400 dark:text-slate-800 text-left'>{text}</p>
        </div>
    )
}
